import { CommonHelperFunctions } from "@kuika/kuika-cl-model";
import Axios from "axios";
import { KMainFunctions } from "../shared/utility/main-functions";

const backendUrl = CommonHelperFunctions.GetBackendUrl();

export interface ILanguagePhrase {
  kuikaId: number;
  propertyName: string;
  tagName: string;
  value: string;
  currentFixedValue?: string;
  isConfirmation?: boolean;
  isNotify?: boolean;
  isConfirmationAction?: boolean;
}

export interface ICollectAllTranslatablePhrasesResponse {
  translationByScreen: Array<{
    screenName: string;
    screenId: string;
    languagePhrases: Array<ILanguagePhrase>;
  }>;
}

export interface ISaveTranslationsRequest {
  languageId: string;
  languageName: string;
  shortName: string;
  isDefault: boolean;
  translationByScreen: Array<{
    screenId: string;
    languagePhrases: Array<ILanguagePhrase>;
  }>;
}

export enum TranslationFilterType {
  All = 0,
  ExcludeNotTranslated = 1,
  ExcludeTranslated = 2
}

interface IExportTranslationsParams {
  language: string;
  translationFilter: TranslationFilterType;
  screenId?: string;
}

export class ScreenDesignTranslationService {
  static async getTranslations(language: string): Promise<ICollectAllTranslatablePhrasesResponse> {
    try {
      const res = await Axios.get(`${backendUrl}/screendesigntranslation/translations`, { params: { language } });
      return res.data;
    } catch (error) {
      KMainFunctions.exceptionHandler(error);
      return { translationByScreen: [] };
    }
  }

  static async saveTranslations(request: ISaveTranslationsRequest): Promise<boolean> {
    try {
      await Axios.post(`${backendUrl}/screendesigntranslation/translations`, request);
      return true;
    } catch (error) {
      KMainFunctions.exceptionHandler(error);
      return false;
    }
  }

  static async exportExcelTemplate(languageName: string): Promise<Blob> {
    try {
      const response = await Axios.get(`${backendUrl}/screendesigntranslation/export-excel-template`, {
        params: { language: languageName },
        responseType: "blob"
      });

      return new Blob([response.data], {
        type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
      });
    } catch (error) {
      KMainFunctions.exceptionHandler(error);
      throw error;
    }
  }

  /** `screenId` narrows the workbook to one screen; omit it to export every screen. */
  static async exportTranslations(
    languageName: string,
    translationFilter: TranslationFilterType = TranslationFilterType.All,
    screenId?: string
  ): Promise<Blob> {
    try {
      const params: IExportTranslationsParams = { language: languageName, translationFilter };
      if (screenId !== undefined) {
        params.screenId = screenId;
      }

      const response = await Axios.get(`${backendUrl}/screendesigntranslation/export-translations`, {
        params,
        responseType: "blob"
      });

      return new Blob([response.data], {
        type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
      });
    } catch (error) {
      KMainFunctions.exceptionHandler(error);
      throw error;
    }
  }

  static async importFromExcel(
    file: File,
    languageId: string,
    languageName: string,
    shortName: string,
    isDefault: boolean
  ): Promise<boolean> {
    try {
      const formData = new FormData();
      formData.append("file", file);

      const params = {
        languageId,
        languageName,
        shortName,
        isDefault
      };

      await Axios.post(`${backendUrl}/screendesigntranslation/import-excel`, formData, {
        params,
        headers: {
          "Content-Type": "multipart/form-data"
        }
      });

      return true;
    } catch (error) {
      KMainFunctions.exceptionHandler(error);
      return false;
    }
  }

  static async deleteAllTranslations(languageId: string): Promise<boolean> {
    try {
      await Axios.delete(`${backendUrl}/screendesigntranslation/translations`, {
        params: { languageId }
      });
      return true;
    } catch (error) {
      KMainFunctions.exceptionHandler(error);
      return false;
    }
  }

  static async deleteScreenTranslations(languageId: string, screenId: string): Promise<boolean> {
    try {
      await Axios.delete(`${backendUrl}/screendesigntranslation/screen-translations`, {
        params: { languageId, screenId }
      });
      return true;
    } catch (error) {
      KMainFunctions.exceptionHandler(error);
      return false;
    }
  }
}
